import { getLabels } from './labels';
import { getEffectiveMode } from './store';
import type { ConsentConfig, ConsentManagerApi, ConsentState } from './types';

const HTMLElementBase = (typeof HTMLElement === 'undefined' ? class {} : HTMLElement) as typeof HTMLElement;

const escapeHtml = (value: string): string => value
  .replaceAll('&', '&amp;')
  .replaceAll('<', '&lt;')
  .replaceAll('>', '&gt;')
  .replaceAll('"', '&quot;')
  .replaceAll("'", '&#039;');

const safeUrl = (value: string): string => {
  try {
    const url = new URL(value, location.origin);
    return ['http:', 'https:'].includes(url.protocol) ? url.href : '#';
  } catch {
    return '#';
  }
};

const safeAssetUrl = (value: string | undefined): string | null => {
  if (!value) return null;
  try {
    const url = new URL(value, location.origin);
    return url.origin === location.origin && ['http:', 'https:'].includes(url.protocol) ? url.href : null;
  } catch {
    return null;
  }
};

const safeCssValue = (value: string | undefined, fallback: string): string => {
  if (!value || value.length > 160 || /[;{}<>]/.test(value)) return fallback;
  return value;
};

const styles = `
  :host {
    --pc-accent: #00c2cb;
    --pc-accent-contrast: #071f3f;
    --pc-surface: #0c2e60;
    --pc-text: #ffffff;
    --pc-muted: #c9d6e8;
    --pc-radius: 18px;
    --pc-font: "Noto Sans JP", "Hiragino Sans", "Yu Gothic", sans-serif;
    --pc-shadow: 0 22px 70px rgba(6, 24, 52, .28), 0 2px 8px rgba(6, 24, 52, .16);
    color-scheme: light;
    font-family: var(--pc-font);
  }
  *, *::before, *::after { box-sizing: border-box; }
  button, input { font: inherit; }
  button:focus-visible, a:focus-visible, input:focus-visible, summary:focus-visible {
    outline: 3px solid #f6c94c;
    outline-offset: 3px;
  }
  .banner {
    position: fixed;
    z-index: 2147483000;
    left: max(20px, env(safe-area-inset-left));
    right: max(20px, env(safe-area-inset-right));
    bottom: max(20px, env(safe-area-inset-bottom));
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 28px;
    width: min(1180px, calc(100% - 40px));
    margin: 0 auto;
    padding: 24px 26px;
    overflow: hidden;
    border: 1px solid rgba(255,255,255,.2);
    border-radius: var(--pc-radius);
    background: linear-gradient(112deg, var(--pc-surface) 0%, #102b50 62%, #12304f 100%);
    box-shadow: var(--pc-shadow);
    color: var(--pc-text);
    animation: pc-rise .38s cubic-bezier(.22,1,.36,1) both;
  }
  .banner::after {
    content: "";
    position: absolute;
    width: 180px;
    height: 180px;
    right: -60px;
    top: -100px;
    border: 36px solid color-mix(in srgb, var(--pc-accent) 22%, transparent);
    border-radius: 50%;
    pointer-events: none;
  }
  :host([data-position="top"]) .banner { top: max(20px, env(safe-area-inset-top)); bottom: auto; }
  .copy { position: relative; display: grid; grid-template-columns: 54px minmax(0,1fr); gap: 18px; align-items: start; }
  .mark { width: 54px; height: 54px; border: 1px solid rgba(255,255,255,.28); border-radius: 16px; display: grid; place-items: center; background: rgba(255,255,255,.08); }
  .mark svg { width: 29px; height: 29px; color: var(--pc-accent); }
  .mark img { display: block; max-width: 38px; max-height: 38px; object-fit: contain; }
  .eyebrow { margin: 0 0 4px; color: var(--pc-accent); font-size: 11px; font-weight: 700; letter-spacing: .16em; }
  h2 { margin: 0 0 7px; color: var(--pc-text); font-size: clamp(20px, 2vw, 25px); line-height: 1.35; letter-spacing: .02em; }
  .description { max-width: 760px; margin: 0; color: var(--pc-muted); font-size: 14px; line-height: 1.8; }
  .description a { color: #fff; text-underline-offset: 3px; }
  .actions { position: relative; z-index: 1; display: grid; grid-template-columns: 1fr 1fr; gap: 10px; width: 330px; align-content: center; }
  .actions.notice-only .btn-all { grid-column: 1 / -1; }
  .btn { min-height: 46px; padding: 10px 16px; border: 1px solid rgba(255,255,255,.76); border-radius: 10px; cursor: pointer; font-size: 13px; font-weight: 700; line-height: 1.4; transition: transform .18s ease, box-shadow .18s ease, background-color .18s ease; }
  .btn:hover { transform: translateY(-1px); }
  .btn-necessary { background: #fff; color: #0c2e60; }
  .btn-all { border-color: var(--pc-accent); background: var(--pc-accent); color: var(--pc-accent-contrast); }
  .btn-settings { grid-column: 1 / -1; background: transparent; color: #fff; }
  .overlay { position: fixed; z-index: 2147483001; inset: 0; display: grid; place-items: center; padding: 24px; background: rgba(4,17,38,.64); }
  .dialog { display: grid; grid-template-rows: auto minmax(0, 1fr) auto; width: min(760px, 100%); max-height: min(820px, calc(100dvh - 40px)); overflow: hidden; border: 1px solid #dce5f0; border-radius: 22px; background: #fff; box-shadow: 0 32px 100px rgba(4,17,38,.32); color: #17233a; animation: pc-scale .24s cubic-bezier(.22,1,.36,1) both; }
  .dialog-head { display: grid; grid-template-columns: 1fr auto; gap: 20px; padding: 26px 28px 21px; border-bottom: 1px solid #e7edf4; background: linear-gradient(135deg, #f9fbff, #f0f7fa); }
  .dialog-head h2 { color: #0c2e60; font-size: 24px; }
  .dialog-head p { margin: 6px 0 0; color: #526179; font-size: 14px; line-height: 1.75; }
  .close { width: 44px; height: 44px; border: 1px solid #cfd9e6; border-radius: 50%; background: #fff; color: #0c2e60; cursor: pointer; font-size: 23px; line-height: 1; }
  .dialog-body { min-height: 0; overflow-y: auto; padding: 20px 28px; }
  .gpc { display: flex; gap: 11px; align-items: flex-start; margin: 0 0 16px; padding: 14px 16px; border: 1px solid #afdfe2; border-radius: 12px; background: #effbfb; color: #174d55; font-size: 13px; line-height: 1.65; }
  .gpc-dot { flex: 0 0 auto; width: 9px; height: 9px; margin-top: 6px; border-radius: 50%; background: #008d95; box-shadow: 0 0 0 5px rgba(0,141,149,.12); }
  .category { margin-bottom: 13px; padding: 18px; border: 1px solid #dde5ef; border-radius: 14px; background: #fff; }
  .category-top { display: grid; grid-template-columns: minmax(0,1fr) auto; gap: 20px; align-items: start; }
  .category h3 { margin: 0 0 5px; color: #0c2e60; font-size: 16px; }
  .category p { margin: 0; color: #5d697d; font-size: 13px; line-height: 1.7; }
  .status { margin-top: 8px; color: #526179; font-size: 11px; font-weight: 700; letter-spacing: .04em; }
  .switch { position: relative; display: inline-flex; align-items: center; min-width: 50px; min-height: 30px; }
  .switch input { position: absolute; width: 1px; height: 1px; opacity: 0; }
  .track { width: 50px; height: 30px; border: 1px solid #aab7c8; border-radius: 999px; background: #e5eaf0; transition: .2s ease; }
  .track::after { content: ""; display: block; width: 22px; height: 22px; margin: 3px; border-radius: 50%; background: #fff; box-shadow: 0 2px 6px rgba(20,39,68,.28); transition: transform .2s ease; }
  .switch input:checked + .track { border-color: #008d95; background: #00a7b0; }
  .switch input:checked + .track::after { transform: translateX(20px); }
  .switch input:focus-visible + .track { outline: 3px solid #f6c94c; outline-offset: 3px; }
  .switch input:disabled + .track { opacity: .78; cursor: not-allowed; }
  details { margin-top: 13px; border-top: 1px solid #edf1f6; padding-top: 12px; }
  summary { cursor: pointer; color: #244569; font-size: 12px; font-weight: 700; }
  .vendor { margin-top: 11px; padding: 12px 13px; border-radius: 10px; background: #f6f8fb; }
  .vendor strong { color: #18365b; font-size: 12px; }
  .vendor p { margin-top: 5px; font-size: 12px; }
  .vendor a { display: inline-block; margin-top: 6px; color: #3455c5; font-size: 12px; text-underline-offset: 3px; }
  .inactive { margin: 12px 0 0; padding: 13px 15px; border-left: 3px solid #8b9bae; background: #f5f7fa; color: #526179; font-size: 12px; line-height: 1.7; }
  .dialog-foot { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; padding: 18px 28px 24px; border-top: 1px solid #e7edf4; background: #fff; }
  .dialog-foot .btn { min-height: 48px; }
  .dialog-foot .btn-necessary { border-color: #0c2e60; background: #fff; }
  .dialog-foot .btn-save { border-color: #0c2e60; background: #0c2e60; color: #fff; }
  .toast { position: fixed; z-index: 2147483002; right: 24px; bottom: 24px; max-width: 390px; padding: 14px 18px; border-radius: 12px; background: #0c2e60; box-shadow: 0 16px 50px rgba(4,17,38,.24); color: #fff; font-size: 13px; line-height: 1.6; animation: pc-rise .25s ease both; }
  .toast-error { background: #8a2432; }
  @keyframes pc-rise { from { opacity: 0; transform: translateY(18px); } to { opacity: 1; transform: translateY(0); } }
  @keyframes pc-scale { from { transform: scale(.975) translateY(8px); } to { transform: scale(1) translateY(0); } }
  @media (max-width: 820px) {
    .banner { grid-template-columns: 1fr; gap: 19px; width: calc(100% - 24px); left: 12px; right: 12px; bottom: max(12px, env(safe-area-inset-bottom)); padding: 20px; }
    :host([data-position="top"]) .banner { top: max(12px, env(safe-area-inset-top)); bottom: auto; }
    .copy { grid-template-columns: 42px minmax(0,1fr); gap: 13px; }
    .mark { width: 42px; height: 42px; border-radius: 12px; }
    .mark svg { width: 23px; height: 23px; }
    .actions { width: 100%; }
    .overlay { align-items: end; padding: 0; }
    .dialog { max-height: calc(100dvh - 12px); border-radius: 20px 20px 0 0; }
    .dialog-head { padding: 21px 20px 17px; }
    .dialog-body { padding: 16px 20px; }
    .dialog-foot { padding: 15px 20px max(18px, env(safe-area-inset-bottom)); }
  }
  @media (max-width: 470px) {
    .actions, .dialog-foot { grid-template-columns: 1fr; }
    .btn-settings { grid-column: auto; }
    .copy { grid-template-columns: 1fr; }
    .mark { display: none; }
    .description { font-size: 13px; }
  }
  @media (prefers-reduced-motion: reduce) {
    *, *::before, *::after { animation-duration: .001ms !important; transition-duration: .001ms !important; }
  }
`;

export class PrivacyConsentCenterElement extends HTMLElementBase {
  private config!: ConsentConfig;
  private manager!: ConsentManagerApi;
  private state: ConsentState | null = null;
  private view: 'banner' | 'preferences' | 'hidden' = 'hidden';
  private previousFocus: HTMLElement | null = null;
  private previousFocusAction: 'preferences' | null = null;
  private toastTimer: number | null = null;

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
  }

  initialize(config: ConsentConfig, manager: ConsentManagerApi, state: ConsentState | null): void {
    this.config = config;
    this.manager = manager;
    this.state = state;
    this.view = state ? 'hidden' : 'banner';
    this.applyTheme();
    this.render();
    this.shadowRoot?.addEventListener('click', this.handleClick);
    this.shadowRoot?.addEventListener('keydown', this.handleKeydown);
  }

  setState(state: ConsentState): void {
    const shouldRestoreFocus = this.view !== 'hidden';
    this.state = state;
    this.view = 'hidden';
    this.render();
    if (shouldRestoreFocus) queueMicrotask(() => this.restoreFocus());
  }

  openPreferences(trigger?: HTMLElement): void {
    const shadowTrigger = trigger ?? (this.shadowRoot?.activeElement instanceof HTMLElement
      ? this.shadowRoot.activeElement
      : null);
    this.previousFocusAction = shadowTrigger?.dataset.action === 'preferences' ? 'preferences' : null;
    this.previousFocus = this.previousFocusAction === null && document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;
    this.view = 'preferences';
    this.render();
    queueMicrotask(() => this.shadowRoot?.querySelector<HTMLElement>('[data-close]')?.focus());
  }

  showToast(): void {
    this.showNotification('saved');
  }

  showError(): void {
    this.showNotification('error');
  }

  private showNotification(notification: 'saved' | 'error'): void {
    if (this.toastTimer) window.clearTimeout(this.toastTimer);
    this.render(notification);
    this.toastTimer = window.setTimeout(() => {
      this.toastTimer = null;
      this.shadowRoot?.querySelector('[data-notification]')?.remove();
    }, 5000);
  }

  private applyTheme(): void {
    const theme = this.config.theme ?? {};
    this.style.setProperty('--pc-accent', safeCssValue(theme.accent, '#00c2cb'));
    this.style.setProperty('--pc-accent-contrast', safeCssValue(theme.accentContrast, '#071f3f'));
    this.style.setProperty('--pc-surface', safeCssValue(theme.surface, '#0c2e60'));
    this.style.setProperty('--pc-text', safeCssValue(theme.text, '#ffffff'));
    this.style.setProperty('--pc-muted', safeCssValue(theme.muted, '#c9d6e8'));
    this.style.setProperty('--pc-radius', safeCssValue(theme.radius, '18px'));
    this.style.setProperty('--pc-font', safeCssValue(theme.fontFamily, '"Noto Sans JP", "Hiragino Sans", "Yu Gothic", sans-serif'));
    this.style.setProperty('--pc-shadow', safeCssValue(theme.shadow, '0 22px 70px rgba(6, 24, 52, .28), 0 2px 8px rgba(6, 24, 52, .16)'));
    this.dataset.position = theme.bannerPosition ?? 'bottom';
  }

  private getStyleTag(): string {
    const nonce = this.config.nonce ? ` nonce="${escapeHtml(this.config.nonce)}"` : '';
    return `<style${nonce}>${styles}</style>`;
  }

  private render(notification: 'saved' | 'error' | null = null): void {
    if (!this.shadowRoot) return;
    const labels = getLabels(this.config.locale, this.config.labels);
    const policyUrl = safeUrl(this.config.policyUrl);
    const icon = `<svg viewBox="0 0 32 32" aria-hidden="true"><path d="M16 3.5 27 8v7.5c0 6.7-4.5 11.2-11 13-6.5-1.8-11-6.3-11-13V8l11-4.5Z" fill="none" stroke="currentColor" stroke-width="2"/><path d="m11.2 16 3 3 6.9-7" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
    const logoUrl = safeAssetUrl(this.config.theme?.logoUrl);
    const brandMark = logoUrl ? `<img src="${escapeHtml(logoUrl)}" alt="">` : icon;

    let content = '';
    if (this.view === 'banner') {
      const noticeOnly = getEffectiveMode(this.config) === 'notice-only';
      content = `
        <section class="banner" role="region" aria-labelledby="pc-banner-title">
          <div class="copy">
            <div class="mark">${brandMark}</div>
            <div>
              <p class="eyebrow">${escapeHtml(labels.eyebrow)}</p>
              <h2 id="pc-banner-title">${escapeHtml(labels.bannerTitle)}</h2>
              <p class="description">${escapeHtml(labels.bannerDescription)} <a href="${policyUrl}">${escapeHtml(labels.privacyPolicy)}</a></p>
            </div>
          </div>
          <div class="actions${noticeOnly ? ' notice-only' : ''}">
            ${noticeOnly
              ? `<button class="btn btn-all" type="button" data-action="reject">${escapeHtml(labels.acknowledge)}</button>`
              : `<button class="btn btn-necessary" type="button" data-action="reject">${escapeHtml(labels.rejectOptional)}</button>
                 <button class="btn btn-all" type="button" data-action="accept">${escapeHtml(labels.acceptAll)}</button>`}
            <button class="btn btn-settings" type="button" data-action="preferences">${escapeHtml(labels.openPreferences)}</button>
          </div>
        </section>`;
    } else if (this.view === 'preferences') {
      const gpc = navigator.globalPrivacyControl === true;
      const categories = this.config.categories.filter((category) => (
        category.required || this.config.vendors.some((vendor) => vendor.categoryId === category.id)
      )).map((category) => {
        const checked = category.required || (this.state
          ? Boolean(this.state.categories[category.id])
          : this.manager.hasConsent(category.id));
        const vendors = this.config.vendors.filter((vendor) => vendor.categoryId === category.id);
        const vendorHtml = vendors.map((vendor) => `
          <div class="vendor">
            <strong>${escapeHtml(vendor.service)} — ${escapeHtml(vendor.company)}</strong>
            <p><b>${escapeHtml(labels.purposes)}:</b> ${escapeHtml(vendor.purposes.join('、'))}</p>
            <p><b>${escapeHtml(labels.dataItems)}:</b> ${escapeHtml(vendor.dataItems.join('、'))}</p>
            ${vendor.removalInstructions ? `<p><b>${escapeHtml(labels.removalInstructions)}:</b> ${escapeHtml(vendor.removalInstructions)}</p>` : ''}
            ${vendor.privacyPolicyUrl ? `<a href="${safeUrl(vendor.privacyPolicyUrl)}" target="_blank" rel="noopener noreferrer">${escapeHtml(labels.privacyPolicy)} ↗</a>` : ''}
          </div>`).join('');
        return `
          <section class="category">
            <div class="category-top">
              <div>
                <h3>${escapeHtml(category.title)}</h3>
                <p>${escapeHtml(category.description)}</p>
                <div class="status">${escapeHtml(category.required ? labels.required : labels.optional)}</div>
              </div>
              <label class="switch" aria-label="${escapeHtml(category.title)}">
                <input type="checkbox" data-category="${escapeHtml(category.id)}" ${checked ? 'checked' : ''} ${category.required || (gpc && category.gpcOptOut) ? 'disabled' : ''}>
                <span class="track" aria-hidden="true"></span>
              </label>
            </div>
            ${vendors.length ? `<details><summary>${escapeHtml(labels.vendors)}（${vendors.length}）</summary>${vendorHtml}</details>` : ''}
          </section>`;
      }).join('');

      content = `
        <div class="overlay">
          <section class="dialog" role="dialog" aria-modal="true" aria-labelledby="pc-dialog-title" aria-describedby="pc-dialog-description">
            <header class="dialog-head">
              <div>
                <p class="eyebrow">${escapeHtml(labels.eyebrow)}</p>
                <h2 id="pc-dialog-title">${escapeHtml(labels.preferencesTitle)}</h2>
                <p id="pc-dialog-description">${escapeHtml(labels.preferencesDescription)}</p>
              </div>
              <button class="close" type="button" data-close aria-label="${escapeHtml(labels.close)}">×</button>
            </header>
            <div class="dialog-body">
              ${gpc ? `<div class="gpc"><span class="gpc-dot"></span><span>${escapeHtml(labels.gpcDetected)}</span></div>` : ''}
              ${categories}
              ${this.config.inactiveNotice ? `<p class="inactive">${escapeHtml(this.config.inactiveNotice)}</p>` : ''}
            </div>
            <footer class="dialog-foot">
              <button class="btn btn-necessary" type="button" data-action="reject">${escapeHtml(labels.rejectOptional)}</button>
              <button class="btn btn-save" type="button" data-action="save">${escapeHtml(labels.savePreferences)}</button>
            </footer>
          </section>
        </div>`;
    }

    const toast = notification === 'saved'
      ? `<div class="toast" role="status" aria-live="polite" data-notification>${escapeHtml(labels.saved)}</div>`
      : notification === 'error'
        ? `<div class="toast toast-error" role="alert" data-notification>${escapeHtml(labels.saveFailed)}</div>`
        : '';
    this.shadowRoot.innerHTML = `${this.getStyleTag()}${content}${toast}`;
  }

  private closePreferences(): void {
    this.view = this.state ? 'hidden' : 'banner';
    this.render();
    queueMicrotask(() => this.restoreFocus());
  }

  private restoreFocus(): void {
    const previousFocus = this.previousFocus;
    const previousFocusAction = this.previousFocusAction;
    this.previousFocus = null;
    this.previousFocusAction = null;

    if (previousFocusAction === 'preferences') {
      const bannerTrigger = this.shadowRoot?.querySelector<HTMLElement>('[data-action="preferences"]');
      if (bannerTrigger) {
        bannerTrigger.focus();
        return;
      }
    }
    if (previousFocus && previousFocus !== this && previousFocus.isConnected) {
      previousFocus.focus();
      return;
    }
    const settingsLink = document.querySelector<HTMLElement>('[data-privacy-settings]');
    if (settingsLink) {
      settingsLink.focus();
      return;
    }
    const pageFallback = document.querySelector<HTMLElement>('main, [role="main"], h1');
    if (!pageFallback) return;
    if (!pageFallback.hasAttribute('tabindex')) pageFallback.tabIndex = -1;
    pageFallback.focus();
  }

  private runAction(action: () => Promise<void>): void {
    void action().catch((error: unknown) => {
      console.error('[PrivacyConsent] 同意状態を更新できませんでした', error);
    });
  }

  private handleClick = (event: Event): void => {
    const target = event.target instanceof Element ? event.target.closest<HTMLElement>('[data-action], [data-close]') : null;
    if (!target) return;
    if (target.hasAttribute('data-close')) {
      this.closePreferences();
      return;
    }
    const action = target.dataset.action;
    if (action === 'accept') this.runAction(() => this.manager.acceptAll());
    if (action === 'reject') {
      if (this.view === 'preferences') this.runAction(() => this.manager.updatePreferences({}, 'preferences'));
      else this.runAction(() => this.manager.rejectOptional());
    }
    if (action === 'preferences') this.openPreferences(target);
    if (action === 'save') {
      const preferences = Object.fromEntries(
        Array.from(this.shadowRoot?.querySelectorAll<HTMLInputElement>('[data-category]') ?? [])
          .map((input) => [input.dataset.category ?? '', input.checked]),
      );
      this.runAction(() => this.manager.updatePreferences(preferences, 'preferences'));
    }
  };

  private handleKeydown = (event: Event): void => {
    const keyboardEvent = event as KeyboardEvent;
    if (this.view !== 'preferences') return;
    if (keyboardEvent.key === 'Escape') {
      keyboardEvent.preventDefault();
      this.closePreferences();
      return;
    }
    if (keyboardEvent.key !== 'Tab') return;
    const focusable = Array.from(this.shadowRoot?.querySelectorAll<HTMLElement>('button:not([disabled]), input:not([disabled]), a[href], summary') ?? []);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = this.shadowRoot?.activeElement;
    if (keyboardEvent.shiftKey && active === first) {
      keyboardEvent.preventDefault();
      last.focus();
    } else if (!keyboardEvent.shiftKey && active === last) {
      keyboardEvent.preventDefault();
      first.focus();
    }
  };
}
